import React, { useState, useEffect } from 'react';
import PhoneFrame from './PhoneFrame';
import BottomNavigation from './BottomNavigation';
import ContentArea from './ContentArea';
import { Home, Book, Search, User, Play } from 'lucide-react';

/**
 * AppPreview component that combines the phone frame, navigation, and content
 * It displays a complete mobile app mockup with custom content based on user selections
 */
const AppPreview = ({ 
  appName = "BookMind",
  logoUrl,
  bookTitles = [],
  primaryColor = "#4f46e5",
  secondaryColor = "#f97316",
  currentScreen,
  screens = [],
  welcomeMessage,
  appDescription,
  splashVideo,
  features = [],
  materialTheme,
  navigateToScreen,
  className = ""
}) => {
  // Use currentScreen from props if provided, otherwise use local state
  const [activeTab, setActiveTab] = useState(currentScreen || 'home');
  
  // Update local state when props change
  useEffect(() => {
    if (currentScreen) {
      setActiveTab(currentScreen);
    }
  }, [currentScreen]);
  
  // Handle tab change and call the navigateToScreen prop if provided
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (navigateToScreen) {
      navigateToScreen(tab);
    }
  };
  
  // Generate screen navigation items based on screens prop or use defaults
  const navItems = screens.length > 0 
    ? screens.map(screen => ({
        id: screen.id,
        label: screen.name,
        icon: getIconForScreen(screen.id)
      }))
    : [
        { id: 'home', label: 'Home', icon: <Home className="w-5 h-5" /> },
        { id: 'library', label: 'Library', icon: <Book className="w-5 h-5" /> },
        { id: 'search', label: 'Search', icon: <Search className="w-5 h-5" /> },
        { id: 'profile', label: 'Profile', icon: <User className="w-5 h-5" /> }
      ];
  
  // Helper function to get icon for screen
  function getIconForScreen(screenId) {
    switch (screenId.toLowerCase()) {
      case 'home':
        return <Home className="w-5 h-5" />;
      case 'library':
      case 'books':
        return <Book className="w-5 h-5" />;
      case 'search':
        return <Search className="w-5 h-5" />;
      case 'profile':
      case 'account':
        return <User className="w-5 h-5" />;
      case 'video':
      case 'media':
        return <Play className="w-5 h-5" />;
      default:
        return <Home className="w-5 h-5" />;
    }
  }

  return (
    <div className={`app-preview ${className}`}>
      <PhoneFrame>
        <ContentArea 
          activeTab={activeTab} 
          bookTitles={bookTitles}
          appName={appName}
          logoUrl={logoUrl}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          welcomeMessage={welcomeMessage}
          appDescription={appDescription}
          splashVideo={splashVideo}
          features={features}
          materialTheme={materialTheme}
          screens={screens}
        />
        <BottomNavigation 
          activeTab={activeTab} 
          setActiveTab={handleTabChange}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          navItems={navItems}
          materialTheme={materialTheme}
        />
      </PhoneFrame>
    </div>
  );
};

export default AppPreview; 